import type { QueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query/queryKeys";

/**
 * Post-write cache sync for the offering page.
 *
 * The page's react-query snapshots (`["slotting", eventId, offeringId, "data", …]`
 * bundle + `[…, "offering"]`) are copied once into the zustand slotting store on
 * mount; every mutation updates only the store. Without invalidation, a remount
 * within staleTime re-seeds the store from the pre-write snapshot — archived
 * requests resurrect (and can be scheduled again), created ones vanish.
 * refetchType "none": the store is the live truth while mounted (WS SYNC +
 * optimistic writes); the next mount refetches.
 */
export function invalidateOfferingData(
  queryClient: QueryClient,
  eventId: string,
  offeringId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ["slotting", eventId, offeringId],
    refetchType: "none",
  });
}

/**
 * Offering LIST caches: the slotting index (qk.slotting.offerings) plus the
 * event dashboard's separate snapshot of the same list
 * (["eventDashboard", eventId, "offerings"], EventDashboardPage). Every
 * offering create/delete/duplicate/rename must hit BOTH — invalidating only
 * the index list leaves the dashboard widget stale for its 30s staleTime.
 * No-op in template mode (no eventId).
 */
export function invalidateOfferingLists(
  queryClient: QueryClient,
  eventId: string,
): void {
  if (!eventId) return;
  void queryClient.invalidateQueries({ queryKey: qk.slotting.offerings(eventId) });
  void queryClient.invalidateQueries({
    queryKey: ["eventDashboard", eventId, "offerings"],
  });
}

/**
 * Offering ENTITY edits (name/dates/bookingMode/sort rules): unlike furniture-
 * level writes, the mounted page derives its calendar window, timezone and
 * request-sidebar presence from the offering query itself — so let the whole
 * offering prefix refetch now (default refetchType) and refresh the list
 * caches that show name/dates.
 */
export function invalidateOfferingSaved(
  queryClient: QueryClient,
  eventId: string,
  offeringId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: ["slotting", eventId, offeringId] });
  invalidateOfferingLists(queryClient, eventId);
}
